import "./UnitTransactionsPanel.css";

import { ReactElement } from "react";

import { EllipseGroupBlueIcon } from "../../assets";
import { ProjectIssuanceConstants } from "../../constants";
import { GetProjectIssuancesResponse } from "../../service/query";
import {
  Button,
  Column,
  CursorDataGrid,
  FlyoutPanel,
  MultiSelect,
  ProfileMenu,
  ProfileMenuItem,
  Row,
} from "../../widget";
import { AddOrLinkIssuanceTransactionPanel, EditIssuanceTransactionPanel } from "./components";
import { useUnitTransactionsPanel } from "./useUnitTransactionsPanel";

export interface UnitTransactionsPanelFilterObjects {
  projectUuid?: string;
  vintage?: number;
  projectTypeDisplayName?: string;
  quantityType?: string;
  habitatGroup?: string;
  habitatCondition?: string;
  distinctiveness?: string;
}

// Provide issuance OR filter
interface UnitTransactionsTaskPanelProps {
  issuance?: GetProjectIssuancesResponse["results"][0];
  filter?: UnitTransactionsPanelFilterObjects;
  show: boolean;
  onClose: () => void;
  handleRefresh: () => void;
}

export const UnitTransactionsPanel = ({
  issuance,
  filter,
  show,
  onClose,
  handleRefresh,
}: UnitTransactionsTaskPanelProps): JSX.Element => {
  const {
    actionsMenuOptionsList,
    issuanceTransaction,
    handleCloseIssuanceTransactionPanel,
    setShowActionsMenu,
    setShowAddOrLinkIssuanceTransactionPanel,
    setShowEditColumnsPanel,
    showEditIssuanceTransactionPanel,
    showAddOrLinkIssuanceTransactionPanel,
    showActionsMenu,
    showEditColumnsPanel,
    filterExportColumns,
    filterExportRows,
    columns,
    exportTable,
    exportFileName,
    defaultEditColumns,
    inventoryTransactionsEditColumnsLocalStorageItemName,
    defaultSortingCriteria,
    dataIsLoading,
    onChange,
    refreshIssuanceTransactions,
    handleRefreshIssuancesAndIssuanceTransactions,
    tagsFilterSelectData,
    selectedTagKeys,
    onTagsFilter,
    hasData,
  } = useUnitTransactionsPanel({
    filter,
    issuance,
    handleRefresh,
  });

  const content = (): ReactElement => (
    <div className="UnitTransactionsPanelContent">
      <div className="UpcomingMilestonesTaskTable">
        <Row spacingV={hasData ? undefined : "3xl"}>
          <Column span={12}>
            <p className="body1">
              If a new transaction completes or cancels a previous reservation or sale, please settle it against the
              existing transaction in the table to keep the inventory accurate.
            </p>
          </Column>
        </Row>
        {!dataIsLoading && (
          <div className={`Filters ${hasData ? "" : "HasNoData"}`}>
            <h3>Filter by</h3>
            <MultiSelect
              label=" "
              className="TagsFilter"
              placeholder="Tags"
              data={tagsFilterSelectData}
              values={selectedTagKeys}
              onChange={(keys) => onTagsFilter(keys)}
            />
            {selectedTagKeys.length > 0 && (
              <Button
                text="Clear tags filter"
                variant="reversedPrimary"
                textUnderline
                textBold
                hasNoPadding
                onClick={() => onTagsFilter([])}
              />
            )}
          </div>
        )}
        <Row spacingV="ml">
          <Column span={12}>
            <CursorDataGrid
              columns={columns}
              clientSide={false}
              filterable
              filterPosition="right"
              onChange={onChange}
              sortable
              data={[]}
              refresh={refreshIssuanceTransactions}
              onExternalExport={exportTable}
              exportFileName={exportFileName}
              filterExportColumns={filterExportColumns}
              filterExportRows={filterExportRows}
              showEditColumnsPanel={showEditColumnsPanel}
              closeEditColumnsPanel={() => setShowEditColumnsPanel(false)}
              editColumnsText="Select the columns to display in the table. Drag headings to rearrange their left-to-right order."
              defaultEditColumns={defaultEditColumns}
              editColumnsLocalStorageItemName={inventoryTransactionsEditColumnsLocalStorageItemName}
              dataIsLoading={dataIsLoading}
              defaultSortingCriteria={defaultSortingCriteria}
              noDataMessage="No issuance transactions to display"
            />
          </Column>
        </Row>
        <Row>
          <Column span={12}>
            <Button text="Close" onClick={onClose} />
          </Column>
        </Row>
      </div>
    </div>
  );

  const buttons = (): ReactElement => (
    <div className="UnitTransactionsPanelButtons">
      {issuance && issuance.status !== ProjectIssuanceConstants.ISSUANCE_STATUS_INACTIVE && (
        <Button text="Add transaction" onClick={() => setShowAddOrLinkIssuanceTransactionPanel(true)} size="medium" />
      )}
      <div>
        <Button
          text="Quick actions⠀"
          icon={<EllipseGroupBlueIcon />}
          variant="tertiary"
          onClick={() => setShowActionsMenu(true)}
          size="smallLarge"
        />
        <ProfileMenu show={showActionsMenu} onClose={() => setShowActionsMenu(false)} position={{ top: "0" }}>
          {actionsMenuOptionsList.map((el) => (
            <ProfileMenuItem
              key={el.id}
              profileMenuItem={el}
              onClick={() => {
                if (el.action) {
                  el.action();
                }
                setShowActionsMenu(false);
              }}
            />
          ))}
        </ProfileMenu>
      </div>
    </div>
  );

  return (
    <FlyoutPanel
      show={show}
      onClose={onClose}
      size="Large"
      header="Unit transactions"
      contentChildren={
        <>
          {content()}
          <AddOrLinkIssuanceTransactionPanel
            issuance={issuance}
            issuanceTransaction={issuanceTransaction}
            show={showAddOrLinkIssuanceTransactionPanel}
            onClose={handleCloseIssuanceTransactionPanel}
            handleRefresh={handleRefreshIssuancesAndIssuanceTransactions}
          />
          {issuanceTransaction && (
            <EditIssuanceTransactionPanel
              issuanceTransaction={issuanceTransaction}
              show={showEditIssuanceTransactionPanel}
              onClose={handleCloseIssuanceTransactionPanel}
              handleRefresh={handleRefreshIssuancesAndIssuanceTransactions}
            />
          )}
        </>
      }
      buttonsChildren={buttons()}
    />
  );
};
